import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "../lib/api";
import AppHeader from "../components/AppHeader";
import CRMTabs from "../components/CRMTabs";

const DOW = ["L", "M", "X", "J", "V", "S", "D"];

function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function endOfMonth(d) { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }
function addMonths(d, n) { return new Date(d.getFullYear(), d.getMonth() + n, 1); }

export default function CRMCalendar() {
  const [events, setEvents] = useState([]);
  const [cursor, setCursor] = useState(startOfMonth(new Date()));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/admin/crm/calendar");
        setEvents(data.events || []);
      } finally { setLoading(false); }
    })();
  }, []);

  const eventsByDay = useMemo(() => {
    const m = {};
    events.forEach((ev) => {
      const d = new Date(ev.datetime);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      m[key] = m[key] || [];
      m[key].push(ev);
    });
    return m;
  }, [events]);

  const days = useMemo(() => {
    const first = startOfMonth(cursor);
    const last = endOfMonth(cursor);
    const out = [];
    // Pad to Monday (ES locale)
    const lead = (first.getDay() + 6) % 7;  // 0=Mon
    for (let i = 0; i < lead; i++) out.push(null);
    for (let day = 1; day <= last.getDate(); day++) {
      out.push(new Date(first.getFullYear(), first.getMonth(), day));
    }
    while (out.length % 7 !== 0) out.push(null);
    return out;
  }, [cursor]);

  const monthLabel = cursor.toLocaleDateString("es-ES", { month: "long", year: "numeric" });

  const upcoming = useMemo(() => {
    const now = Date.now();
    return events
      .filter((e) => new Date(e.datetime).getTime() >= now - 86_400_000)
      .slice(0, 12);
  }, [events]);

  return (
    <div className="min-h-screen bg-stone-950 text-stone-100">
      <AppHeader />
      <div className="border-b border-stone-800 bg-stone-900 px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center gap-4">
          <h1 className="font-display text-2xl font-black tracking-tight text-white">Calendario</h1>
          <div className="ml-auto flex items-center gap-2">
            <button onClick={() => setCursor(addMonths(cursor, -1))} className="bg-stone-800 hover:bg-stone-700 p-2 rounded-sm" data-testid="cal-prev"><ChevronLeft className="w-4 h-4" /></button>
            <span className="font-display font-bold text-lg capitalize px-3 min-w-[180px] text-center" data-testid="cal-label">{monthLabel}</span>
            <button onClick={() => setCursor(addMonths(cursor, 1))} className="bg-stone-800 hover:bg-stone-700 p-2 rounded-sm" data-testid="cal-next"><ChevronRight className="w-4 h-4" /></button>
            <button onClick={() => setCursor(startOfMonth(new Date()))} className="bg-stone-800 hover:bg-stone-700 px-3 py-2 text-xs uppercase tracking-[0.18em] font-bold rounded-sm">Hoy</button>
          </div>
        </div>
      </div>

      <CRMTabs />

      <main className="max-w-6xl mx-auto px-6 py-6 grid lg:grid-cols-4 gap-6">
        <div className="lg:col-span-3 bg-stone-900 border border-stone-800 rounded-sm overflow-hidden">
          <div className="grid grid-cols-7 border-b border-stone-800 bg-stone-800/50">
            {DOW.map((d) => <div key={d} className="text-center text-[10px] tracking-[0.3em] uppercase font-bold text-stone-400 py-2">{d}</div>)}
          </div>
          <div className="grid grid-cols-7" data-testid="calendar-grid">
            {days.map((d, i) => {
              if (!d) return <div key={i} className="bg-stone-900/50 min-h-[100px] border-r border-b border-stone-800" />;
              const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
              const evs = eventsByDay[key] || [];
              const today = new Date();
              const isToday = d.toDateString() === today.toDateString();
              return (
                <div key={i} className={`min-h-[100px] border-r border-b border-stone-800 p-1.5 ${isToday ? "bg-[#E0651F]/10" : ""}`}>
                  <div className={`text-xs font-bold mb-1 ${isToday ? "text-[#E0651F]" : "text-stone-400"}`}>{d.getDate()}</div>
                  <div className="space-y-1">
                    {evs.slice(0, 3).map((ev) => (
                      <Link key={`${ev.card_id}-${ev.type}`} to={`/admin/crm/${ev.card_id}`}
                        className={`block text-[10px] truncate px-1.5 py-0.5 rounded-sm hover:opacity-80 ${ev.type === "auction" ? "bg-[#E0651F] text-white" : "bg-amber-700 text-white"}`}>
                        {new Date(ev.datetime).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })} · {ev.title.replace(/^📌 |^📞 /, "")}
                      </Link>
                    ))}
                    {evs.length > 3 && <div className="text-[10px] text-stone-500">+{evs.length - 3} más</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <aside className="lg:col-span-1 bg-stone-900 border border-stone-800 rounded-sm p-4">
          <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#E0651F] mb-3">Próximos eventos</div>
          {loading ? <div className="text-xs text-stone-500">Cargando…</div> : (
            <div className="space-y-2">
              {upcoming.length === 0 && <div className="text-xs text-stone-600 italic">Sin eventos próximos</div>}
              {upcoming.map((ev) => (
                <Link key={`${ev.card_id}-${ev.type}-${ev.datetime}`} to={`/admin/crm/${ev.card_id}`}
                  className="block bg-stone-800 hover:bg-stone-700 border-l-2 border-[#E0651F] p-2 rounded-sm">
                  <div className="text-[10px] text-stone-400 mb-0.5">{new Date(ev.datetime).toLocaleString("es-ES", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</div>
                  <div className="text-xs text-white truncate">{ev.title}</div>
                </Link>
              ))}
            </div>
          )}
        </aside>
      </main>
    </div>
  );
}
